"use client";

import { useCallback, useEffect, useRef, useState } from "react";
import Image from "next/image";
import { tinaField } from "tinacms/dist/react";
import type { PagesBlocksHero } from "@/tina/__generated__/types";

const AUTOPLAY_MS = 6000;

export default function Hero({ data }: { data: PagesBlocksHero }) {
  const slides = (data.slides ?? []).filter((s): s is NonNullable<typeof s> => !!s);
  // Slides are stacked absolute+opacity for the crossfade, so every slide's
  // media sits within the viewport bounding box regardless of visibility —
  // native lazy-loading can't tell an opacity-0 slide apart from a visible
  // one. `loaded` tracks which slides have ever been active (plus a
  // one-ahead preload) so the rest genuinely don't mount/fetch until
  // needed. Bundled into `index`'s own setState call rather than a
  // separate effect, since deriving one piece of state from another inside
  // an effect just to setState again causes an extra cascading render.
  const [{ index, loaded }, setSlide] = useState(() => {
    const loaded = new Set<number>([0]);
    if (slides.length > 1) loaded.add(1 % slides.length);
    return { index: 0, loaded };
  });

  const goTo = useCallback(
    (next: number) => {
      setSlide((prev) => {
        const preload = (next + 1) % slides.length;
        if (prev.loaded.has(next) && prev.loaded.has(preload)) {
          return { index: next, loaded: prev.loaded };
        }
        const loaded = new Set(prev.loaded);
        loaded.add(next);
        loaded.add(preload);
        return { index: next, loaded };
      });
    },
    [slides.length]
  );

  // Re-arms on every index change, whether that change came from autoplay
  // or a manual click — so clicking a dot/arrow doesn't get immediately
  // undone by a timer that was already mid-flight.
  useEffect(() => {
    if (slides.length < 2) return;
    const timer = setInterval(() => {
      goTo((index + 1) % slides.length);
    }, AUTOPLAY_MS);
    return () => clearInterval(timer);
  }, [index, slides.length, goTo]);

  // Playback can't be left to the `autoPlay` attribute alone: it's only
  // honored by the browser at the moment a <video> first mounts (or its
  // data becomes ready) with the attribute already set. The one-slide-ahead
  // preload mounts with `autoPlay={false}` (see below), so when the
  // carousel later reaches it and its `autoPlay` prop flips to `true`,
  // Chrome doesn't retroactively start playback — the element just sits
  // paused on whatever frame its `preload="metadata"` warm-up buffered.
  // Confirmed live: every slide past the first loaded its poster/first
  // frame and stayed frozen there. Driving `.play()`/`.pause()` imperatively
  // here is the only way a slide actually starts once it becomes active.
  const videoRefs = useRef<(HTMLVideoElement | null)[]>([]);
  useEffect(() => {
    videoRefs.current.forEach((video, i) => {
      if (!video) return;
      if (i === index) {
        video.play().catch(() => {});
      } else {
        video.pause();
      }
    });
  }, [index]);

  if (slides.length === 0) return null;

  return (
    <section className="px-hero relative isolate min-h-[85vh] w-full overflow-hidden">
      {slides.map((slide, i) => (
        <div
          key={i}
          data-tina-field={tinaField(slide)}
          aria-hidden={i !== index}
          className={`absolute inset-0 transition-opacity duration-700 ease-in-out ${
            i === index ? "z-10 opacity-100" : "z-0 opacity-0 pointer-events-none"
          }`}
        >
          {slide.video ? (
            loaded.has(i) && (
              // Only the truly active slide plays (driven imperatively by
              // the effect above, not the `autoPlay` attribute — see its
              // comment). The one-slide-ahead preload only warms up
              // `preload="metadata"`; a muted autoplaying video buffers
              // regardless of that hint, so leaving `autoPlay` off here is
              // what keeps a not-yet-active slide from also fully
              // downloading on page load.
              <video
                ref={(el) => {
                  videoRefs.current[i] = el;
                }}
                src={slide.video}
                // next/image's optimizer only serves widths listed in next.config.ts's
                // images.deviceSizes/imageSizes (default set, unmodified here) — 1080 is
                // one of those buckets; an arbitrary width gets a hard 400
                // ("w parameter is not allowed"). Sized for mobile (where Lighthouse/Speed
                // Index actually measures this) rather than desktop retina, since this is a
                // placeholder the video replaces within about a second, not final content —
                // confirmed live that the unthrottled poster request alone (at the old
                // w=1920&q=50) transferred ~153KB and took ~520ms, competing with the page's
                // own JS/CSS for bandwidth on the critical path.
                poster={slide.image ? `/_next/image?url=${encodeURIComponent(slide.image)}&w=1080&q=40` : undefined}
                data-tina-field={tinaField(slide, "video")}
                className="absolute inset-0 h-full w-full object-cover"
                loop
                muted
                playsInline
                preload={i === index ? "auto" : "metadata"}
                {...({ fetchPriority: i === index ? "high" : "low" } as Record<string, string>)}
              />
            )
          ) : (
            slide.image &&
            loaded.has(i) && (
              <Image
                src={slide.image}
                alt={slide.imageAlt || ""}
                data-tina-field={tinaField(slide, "image")}
                fill
                sizes="100vw"
                priority={i === 0}
                className="object-cover"
              />
            )
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-black/75 via-black/40 to-black/10" />

          <div className="absolute inset-0 flex flex-col items-center justify-center px-4 text-center text-white">
            {slide.heading && (
              <h2
                data-tina-field={tinaField(slide, "heading")}
                className="max-w-3xl text-4xl font-semibold text-balance sm:text-5xl"
              >
                {slide.heading}
              </h2>
            )}
            {slide.subheading && (
              <p
                data-tina-field={tinaField(slide, "subheading")}
                className="mt-4 max-w-2xl text-lg text-white/90"
              >
                {slide.subheading}
              </p>
            )}
            {slide.buttonLabel && slide.buttonUrl && (
              <a
                href={slide.buttonUrl}
                data-tina-field={tinaField(slide, "buttonLabel")}
                className="mt-8 inline-block rounded bg-accent px-6 py-3 text-sm font-medium text-accent-foreground hover:opacity-90"
              >
                {slide.buttonLabel}
              </a>
            )}
          </div>
        </div>
      ))}

      {slides.length > 1 && (
        <>
          <button
            type="button"
            aria-label="Previous slide"
            onClick={() => goTo((index - 1 + slides.length) % slides.length)}
            className="absolute top-1/2 left-4 z-20 -translate-y-1/2 rounded-full bg-black/30 p-2 text-white hover:bg-black/50"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="h-5 w-5">
              <path d="M15 18l-6-6 6-6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
          <button
            type="button"
            aria-label="Next slide"
            onClick={() => goTo((index + 1) % slides.length)}
            className="absolute top-1/2 right-4 z-20 -translate-y-1/2 rounded-full bg-black/30 p-2 text-white hover:bg-black/50"
          >
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={2} className="h-5 w-5">
              <path d="M9 6l6 6-6 6" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>

          <div className="absolute bottom-6 left-1/2 z-20 flex -translate-x-1/2 gap-2">
            {slides.map((_, i) => (
              <button
                key={i}
                type="button"
                aria-label={`Go to slide ${i + 1}`}
                onClick={() => goTo(i)}
                className="group flex h-6 w-6 items-center justify-center"
              >
                <span
                  className={`h-2 w-2 rounded-full transition ${
                    i === index ? "bg-white" : "bg-white/40 group-hover:bg-white/70"
                  }`}
                />
              </button>
            ))}
          </div>
        </>
      )}
    </section>
  );
}
